import { FC, useCallback } from 'react';
import { Flex, Box } from '@rebass/emotion';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { openModal, closeModal } from '@app/redux/modal';
import Card from '@app/components/Card';
import { H4 } from '@app/shop/components/typography';
import FormattedCountryName from '@app/shop/components/FormattedCountryName';
import { UnstyledButton } from '@app/shop/components/Button';
import useCart from '@app/shop/hooks/useCart';
import EditIcon from '@app/shop/icons/EditIcon';
import Checkmark from '@app/components/Checkmark';
import { AppTheme } from '@app/styles/theme';
import { rem } from '@app/styles/theme/layout';

import EditAddressForm from './EditAddressForm';
import { AddressType } from '../Address.types';
import messages from '../Address.messages';

const AddressCard: FC<{
  address: AddressType;
  selected?: boolean;
  hasError?: boolean;
  idx?: number;
  onSelect: () => void;
}> = ({ address, selected, hasError, onSelect, idx }) => {
  const dispatch = useDispatch();
  const { cartId, isVirtual } = useCart();
  const openAddressForm = useCallback(
    () =>
      dispatch(
        openModal({
          label: 'edit-address',
          content: (
            <EditAddressForm
              selected={selected}
              cart={{ cartId: cartId || '', isVirtual }}
              address={address}
              onClose={() => dispatch(closeModal())}
            />
          ),
        })
      ),
    [dispatch, address, cartId, isVirtual, selected]
  );

  return (
    <Box
      width={[1, 1 / 2]}
      pr={[0, 10]}
      mb={20}
      css={{ position: 'relative' }}
      data-testid={`address-${idx}`}
    >
      <UnstyledButton
        data-testid="edit-address-button"
        title="Edit"
        onClick={openAddressForm}
        css={{ position: 'absolute', right: '30px', top: '22px', zIndex: 1 }}
      >
        <EditIcon />
      </UnstyledButton>

      <Card
        data-testid={selected ? 'address-card-selected' : 'address-card'}
        highlight={selected}
        disabled={!selected}
        hasError={hasError}
        onClick={onSelect}
      >
        <Flex>
          {selected && (
            <Checkmark
              color="light-blue"
              css={css`
                vertical-align: top;
                margin-top: 5px;
                margin-right: 15px;
              `}
            />
          )}

          <H4
            css={css`
              margin-top: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            `}
          >
            {address.firstname} {address.lastname}
          </H4>
        </Flex>

        <Address>
          {address.street?.map((street, index) => (
            <div key={index}>{street}</div>
          ))}
          {address.postcode} {address.city}
          <br />
          <FormattedCountryName code={address.country_code} />
        </Address>

        {address.vat_id && (
          <VATLabel>
            <FormattedMessage {...messages.businessLabel} />
          </VATLabel>
        )}
      </Card>
    </Box>
  );
};

export default AddressCard;

const VATLabel = styled.span`
  color: ${({ theme }) => (theme as AppTheme).colors.lightBlue};
  border: 1px solid ${({ theme }) => (theme as AppTheme).colors.lightBlue};
  padding: ${rem(3)} ${rem(6)};
  font-family: ${({ theme }) => (theme as AppTheme).fonts.fontBold};
  font-size: ${rem(12)};
  border-radius: ${({ theme }) => (theme as AppTheme).radii.normal};
  display: inline-block;
  position: absolute;
  font-weight: bold;
  text-transform: uppercase;
  right: ${rem(30)};
  bottom: ${rem(21)};
`;

const Address = styled.address`
  font-style: normal;
`;
